import type {
  StrategyRdMatchSr,
  StrategyRdSrLevel,
  StrategyRdSrOpposing,
  StrategyRdSrRail,
} from "../types/strategyRd";
import type { StrategyRdConceptDrawing } from "./strategyRdConceptDrawings";
import type { StrategyRdConceptOhlcLike } from "./strategyRdConceptDrawings";
import {
  normalizeStrategyRdSrScopes,
  type StrategyRdSrScope,
} from "./strategyRdConcepts";

const SR_LABELS: Record<StrategyRdSrScope, { high: string; low: string }> = {
  day: { high: "PDH", low: "PDL" },
  week: { high: "PWH", low: "PWL" },
  month: { high: "PMH", low: "PML" },
};

type PeriodBucket = {
  key: string;
  firstTime: string;
  lastTime: string;
  high: number;
  low: number;
};

function candleTime(c: StrategyRdConceptOhlcLike): string | null {
  const t = (c.timestamp || c.time || "").trim();
  return t || null;
}

function candleMs(c: StrategyRdConceptOhlcLike): number | null {
  const t = candleTime(c);
  if (!t) return null;
  const ms = Date.parse(t);
  return Number.isFinite(ms) ? ms : null;
}

function zonedParts(
  ms: number,
  timeZone: string
): { year: number; month: number; day: number } | null {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: timeZone.trim() || "UTC",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(new Date(ms));
    const num = (type: string): number => {
      const raw = parts.find((p) => p.type === type)?.value;
      return Number(raw);
    };
    const year = num("year");
    const month = num("month");
    const day = num("day");
    if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null;
    return { year, month, day };
  } catch {
    if ((timeZone.trim() || "UTC") === "UTC") return null;
    return zonedParts(ms, "UTC");
  }
}

/** ISO week key from a civil date (timezone-local Y-M-D). */
export function strategyRdIsoWeekKey(year: number, month: number, day: number): string {
  const date = new Date(Date.UTC(year, month - 1, day));
  const dow = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - dow);
  const isoYear = date.getUTCFullYear();
  const yearStart = Date.UTC(isoYear, 0, 1);
  const week = Math.ceil((date.getTime() - yearStart) / 86400000 / 7 + 1);
  return `${isoYear}-W${String(week).padStart(2, "0")}`;
}

export function strategyRdSrPeriodKey(
  ms: number,
  scope: StrategyRdSrScope,
  timeZone = "UTC"
): string | null {
  const parts = zonedParts(ms, timeZone);
  if (!parts) return null;
  const pad = (n: number) => String(n).padStart(2, "0");
  if (scope === "day") return `${parts.year}-${pad(parts.month)}-${pad(parts.day)}`;
  if (scope === "month") return `${parts.year}-${pad(parts.month)}`;
  return strategyRdIsoWeekKey(parts.year, parts.month, parts.day);
}

function bucketsForScope(
  candles: readonly StrategyRdConceptOhlcLike[],
  scope: StrategyRdSrScope,
  timeZone: string
): PeriodBucket[] {
  const byKey = new Map<string, PeriodBucket>();
  const order: string[] = [];
  for (const candle of candles) {
    const time = candleTime(candle);
    const ms = candleMs(candle);
    if (!time || ms == null) continue;
    const key = strategyRdSrPeriodKey(ms, scope, timeZone);
    if (!key) continue;
    const high = Number(candle.high);
    const low = Number(candle.low);
    if (!Number.isFinite(high) || !Number.isFinite(low)) continue;
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, { key, firstTime: time, lastTime: time, high, low });
      order.push(key);
      continue;
    }
    existing.lastTime = time;
    existing.high = Math.max(existing.high, high);
    existing.low = Math.min(existing.low, low);
  }
  return order.map((key) => byKey.get(key)!).filter(Boolean);
}

export type StrategyRdPriorSrLevel = {
  scope: StrategyRdSrScope;
  fromBarTime: string;
  toBarTime: string;
  high: number;
  low: number;
};

/** Prior completed day/week/month high–low applied across the next period. */
export function strategyRdPriorSrLevels(
  candles: readonly StrategyRdConceptOhlcLike[] | null | undefined,
  scope: StrategyRdSrScope,
  timeZone = "UTC"
): StrategyRdPriorSrLevel[] {
  const periods = bucketsForScope(candles ?? [], scope, timeZone);
  const out: StrategyRdPriorSrLevel[] = [];
  for (let i = 1; i < periods.length; i += 1) {
    const prior = periods[i - 1]!;
    const current = periods[i]!;
    if (!(prior.high > prior.low)) continue;
    out.push({
      scope,
      fromBarTime: current.firstTime,
      toBarTime: current.lastTime,
      high: prior.high,
      low: prior.low,
    });
  }
  return out;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function asIso(value: unknown): string | null {
  const s = typeof value === "string" ? value.trim() : "";
  return s || null;
}

function finiteNum(value: unknown): number | null {
  // Number(null) === 0 — never coerce null/undefined/"" into a fake price.
  if (value == null || value === "") return null;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

function parseOpposing(raw: unknown): StrategyRdSrOpposing | null {
  const row = asRecord(raw);
  if (!row) return null;
  const high = finiteNum(row.high);
  const low = finiteNum(row.low);
  if (high == null || low == null || !(high > low)) return null;
  const sideRaw = String(row.side ?? "").trim().toLowerCase();
  const side: StrategyRdSrOpposing["side"] = sideRaw === "resistance" ? "resistance" : "support";
  return { side, high, low };
}

function parseSrRailLike(raw: unknown): StrategyRdSrLevel | null {
  const row = asRecord(raw);
  if (!row) return null;
  const scope = normalizeStrategyRdSrScopes([row.scope])[0];
  const high = finiteNum(row.high);
  const low = finiteNum(row.low);
  const validFrom = asIso(row.valid_from);
  const validTo = asIso(row.valid_to);
  if (!scope || high == null || low == null || !validFrom || !validTo) return null;
  if (!(high > low)) return null;
  const names = SR_LABELS[scope];
  return {
    scope,
    high,
    low,
    high_label: String(row.high_label ?? "").trim() || names.high,
    low_label: String(row.low_label ?? "").trim() || names.low,
    valid_from: validFrom,
    valid_to: validTo,
    period_start: asIso(row.period_start),
    period_end: asIso(row.period_end),
    opposing: parseOpposing(row.opposing),
  };
}

export function parseStrategyRdSrRails(raw: unknown): StrategyRdSrRail[] {
  if (!Array.isArray(raw)) return [];
  const out: StrategyRdSrRail[] = [];
  for (const item of raw) {
    const row = parseSrRailLike(item);
    if (!row) continue;
    out.push({
      scope: row.scope,
      high: row.high,
      low: row.low,
      high_label: row.high_label,
      low_label: row.low_label,
      valid_from: row.valid_from,
      valid_to: row.valid_to,
      period_start: row.period_start,
      period_end: row.period_end,
    });
  }
  return out;
}

export function parseStrategyRdMatchSr(meta: unknown): StrategyRdMatchSr | null {
  const root = asRecord(meta);
  const sr = asRecord(root?.sr);
  if (!sr) return null;
  const levels = Array.isArray(sr.levels)
    ? sr.levels.map(parseSrRailLike).filter((row): row is StrategyRdSrLevel => row != null)
    : [];
  const scopes = normalizeStrategyRdSrScopes(
    Array.isArray(sr.scopes) ? sr.scopes : levels.map((row) => row.scope)
  );
  const status = String(sr.status ?? "").trim() || (levels.length ? "ok" : "levels_missing");
  return {
    scopes,
    timezone: typeof sr.timezone === "string" ? sr.timezone : null,
    week_start: typeof sr.week_start === "string" ? sr.week_start : null,
    status,
    levels,
  };
}

function barTimeMs(value: string | null | undefined): number | null {
  const t = (value || "").trim().replace(/\+00:00$/, "Z");
  if (!t) return null;
  const ms = Date.parse(t);
  return Number.isFinite(ms) ? ms : null;
}

/** Keep S/R geometry near the setup — not the full prior-period validity span. */
function clipSrSpanAroundSetup(opts: {
  validFrom: string;
  validTo: string;
  setupBarTime?: string | null;
  entryBarTime?: string | null;
  /** Bars of padding on each side when candle duration is unknown (ms). Default ~12h. */
  padMs?: number;
}): { fromBarTime: string; toBarTime: string; barTime: string } {
  const setupMs = barTimeMs(opts.setupBarTime);
  const entryMs = barTimeMs(opts.entryBarTime);
  const validFromMs = barTimeMs(opts.validFrom);
  const validToMs = barTimeMs(opts.validTo);
  const pad = opts.padMs ?? 12 * 60 * 60 * 1000;

  if (setupMs == null) {
    return {
      fromBarTime: opts.validFrom,
      toBarTime: opts.validTo,
      barTime: opts.validFrom,
    };
  }

  const anchorEnd = entryMs != null && entryMs >= setupMs ? entryMs : setupMs;
  let fromMs = setupMs - pad;
  let toMs = anchorEnd + pad;
  if (validFromMs != null) fromMs = Math.max(fromMs, validFromMs);
  if (validToMs != null) toMs = Math.min(toMs, validToMs);
  if (toMs < fromMs) {
    fromMs = setupMs - pad;
    toMs = anchorEnd + pad;
  }
  return {
    fromBarTime: new Date(fromMs).toISOString(),
    toBarTime: new Date(toMs).toISOString(),
    barTime: opts.setupBarTime!.trim(),
  };
}

function railCoversSetup(
  rail: Pick<StrategyRdSrRail, "valid_from" | "valid_to">,
  setupBarTime: string | null | undefined
): boolean {
  const setupMs = barTimeMs(setupBarTime);
  if (setupMs == null) return true;
  const fromMs = barTimeMs(rail.valid_from);
  const toMs = barTimeMs(rail.valid_to);
  if (fromMs == null || toMs == null) return true;
  return setupMs >= fromMs && setupMs <= toMs;
}

function pushRailPair(
  out: StrategyRdConceptDrawing[],
  rail: StrategyRdSrRail,
  showLabel: boolean,
  span?: { fromBarTime: string; toBarTime: string; barTime: string }
): void {
  const suffix = `${rail.scope}:${rail.valid_from}`;
  const fromBarTime = span?.fromBarTime ?? rail.valid_from;
  const toBarTime = span?.toBarTime ?? rail.valid_to;
  const barTime = span?.barTime ?? rail.valid_from;
  out.push({
    id: `sr:${suffix}:high`,
    label: rail.high_label,
    kind: "level",
    barTime,
    fromBarTime,
    toBarTime,
    high: rail.high,
    low: rail.high,
    price: rail.high,
    showLabel,
  });
  out.push({
    id: `sr:${suffix}:low`,
    label: rail.low_label,
    kind: "level",
    barTime,
    fromBarTime,
    toBarTime,
    high: rail.low,
    low: rail.low,
    price: rail.low,
    showLabel,
  });
}

/**
 * Opposing S/R for the focused setup only.
 * Prefer `matches[].meta.sr` (levels at that bar). Chart-wide `sr_rails` are
 * filtered to rails that cover the setup and clipped near setup→entry —
 * never dump every period across the lookback window.
 */
export function strategyRdOpposingSrDrawings(opts: {
  rails?: readonly StrategyRdSrRail[] | null;
  matchSr?: StrategyRdMatchSr | null;
  matchMeta?: unknown;
  setupBarTime?: string | null;
  entryBarTime?: string | null;
}): StrategyRdConceptDrawing[] {
  const matchSr = opts.matchSr ?? parseStrategyRdMatchSr(opts.matchMeta);
  const setupBar =
    (opts.setupBarTime || "").trim() ||
    null;
  const entryBar = (opts.entryBarTime || "").trim() || null;

  // Focused match carries the levels evaluated at that setup — preferred source.
  const matchLevels = matchSr?.levels ?? [];
  const railsFromMatch = matchLevels.map((level) => ({
    scope: level.scope,
    high: level.high,
    low: level.low,
    high_label: level.high_label,
    low_label: level.low_label,
    valid_from: level.valid_from,
    valid_to: level.valid_to,
    period_start: level.period_start,
    period_end: level.period_end,
  }));

  const windowRails = (opts.rails ?? []) as StrategyRdSrRail[];
  const rails: StrategyRdSrRail[] =
    railsFromMatch.length > 0
      ? railsFromMatch
      : setupBar
        ? windowRails.filter((rail) => railCoversSetup(rail, setupBar))
        : [];

  if (!rails.length && matchLevels.length === 0) return [];

  const out: StrategyRdConceptDrawing[] = [];
  const labeled = new Set<string>();
  rails.forEach((rail) => {
    const span = clipSrSpanAroundSetup({
      validFrom: rail.valid_from,
      validTo: rail.valid_to,
      setupBarTime: setupBar,
      entryBarTime: entryBar,
    });
    const show = !labeled.has(`${rail.high_label}|${rail.low_label}`);
    labeled.add(`${rail.high_label}|${rail.low_label}`);
    pushRailPair(out, rail, show, span);
  });

  let opposingLabeled = false;
  matchLevels.forEach((level) => {
    const band = level.opposing;
    if (!band || !(band.high > band.low)) return;
    const span = clipSrSpanAroundSetup({
      validFrom: level.valid_from,
      validTo: level.valid_to,
      setupBarTime: setupBar,
      entryBarTime: entryBar,
    });
    out.push({
      id: `sr:${level.scope}:${level.valid_from}:opposing`,
      label: "Opposing S/R",
      kind: "zone",
      barTime: span.barTime,
      fromBarTime: span.fromBarTime,
      toBarTime: span.toBarTime,
      high: band.high,
      low: band.low,
      price: (band.high + band.low) / 2,
      showLabel: !opposingLabeled,
    });
    opposingLabeled = true;
  });
  return out;
}

export function strategyRdSrLegendItems(
  _srScopes: readonly string[] | null | undefined,
  _confluences: readonly string[] | null | undefined,
  rails?: readonly StrategyRdSrRail[] | null,
  matchSr?: StrategyRdMatchSr | null
): Array<{ title: string; color: string }> {
  const fromRails = Array.from(new Set((rails ?? []).map((r) => r.scope)));
  const fromLevels = Array.from(new Set((matchSr?.levels ?? []).map((row) => row.scope)));
  const scopes: StrategyRdSrScope[] = fromRails.length > 0 ? fromRails : fromLevels;
  const out: Array<{ title: string; color: string }> = [];
  if (scopes.includes("day")) {
    out.push({ title: "PDH", color: "#E00101" }, { title: "PDL", color: "#06E363" });
  }
  if (scopes.includes("week")) {
    out.push({ title: "PWH", color: "#E00101" }, { title: "PWL", color: "#06E363" });
  }
  if (scopes.includes("month")) {
    out.push({ title: "PMH", color: "#E00101" }, { title: "PML", color: "#06E363" });
  }
  return out;
}